import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, 
  Sparkles, 
  TrendingUp, 
  Calendar,
  MapPin,
  Users,
  Clock,
  ChevronRight,
  Gamepad2,
  Music,
  Laugh,
  Dumbbell,
  Utensils,
  Heart,
  Laptop,
  Search,
  LayoutDashboard
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categoryIcons = {
  gaming: Gamepad2,
  music: Music,
  comedy: Laugh,
  fitness: Dumbbell,
  food: Utensils,
  lifestyle: Heart,
  tech: Laptop,
  beauty: Sparkles
};

// Sample events matching the reference design
const sampleEvents = [
  {
    id: '1',
    title: 'Gaming Stream Night',
    influencer_name: 'AlexGamer',
    category: 'gaming',
    event_date: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Virtual',
    image_url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&h=300&fit=crop',
    total_tickets: 100,
    sold_tickets: 78,
    ticket_tiers: [{ name: 'General', price: 25, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '2',
    title: 'Beauty Masterclass',
    influencer_name: 'Mia Glam',
    category: 'beauty',
    event_date: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Los Angeles, CA',
    image_url: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400&h=300&fit=crop',
    total_tickets: 50,
    sold_tickets: 35,
    ticket_tiers: [{ name: 'VIP', price: 150, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '3',
    title: 'Live Podcast Show',
    influencer_name: 'Jake & Tom',
    category: 'comedy',
    event_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'New York, NY',
    image_url: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400&h=300&fit=crop',
    total_tickets: 200,
    sold_tickets: 180,
    ticket_tiers: [{ name: 'General', price: 45, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '4',
    title: 'Concert with DJ SonicWave',
    influencer_name: 'DJ SonicWave',
    category: 'music',
    event_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Miami, FL',
    image_url: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&h=300&fit=crop',
    total_tickets: 500,
    sold_tickets: 420,
    ticket_tiers: [{ name: 'General', price: 75, sale_mode: 'fixed' }],
    status: 'published'
  },
  {
    id: '5',
    title: 'DJ SonicWave',
    influencer_name: 'DJ SonicWave',
    description: 'Concert in Precsion!',
    category: 'music',
    event_date: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Las Vegas',
    image_url: 'https://images.unsplash.com/photo-1571266028243-3716f02d2d2e?w=400&h=300&fit=crop',
    total_tickets: 300,
    sold_tickets: 150,
    ticket_tiers: [{ name: 'General', price: 60, sale_mode: 'fixed' }],
    status: 'published'
  }
];

const smallIntimateEvents = [
  {
    id: '6',
    title: 'Dinner with LifestyleLexi',
    influencer_name: 'LifestyleLexi',
    category: 'lifestyle',
    event_date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Chicago, IL',
    image_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop',
    total_tickets: 8,
    sold_tickets: 6,
    ticket_tiers: [{ name: 'Dinner Seat', price: 500, sale_mode: 'fixed' }],
    status: 'published',
    tag: 'Limited Spots'
  },
  {
    id: '7',
    title: 'Coffee Chat',
    influencer_name: 'FitBro',
    category: 'fitness',
    event_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Austin, TX',
    image_url: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=300&fit=crop',
    total_tickets: 10,
    sold_tickets: 9,
    ticket_tiers: [{ name: 'Participant', price: 75, sale_mode: 'fixed' }],
    status: 'published',
    tag: '1 Limited Spots'
  },
  {
    id: '8',
    title: 'Wine Tasting',
    influencer_name: 'TravelDana',
    category: 'lifestyle',
    event_date: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Napa Valley',
    image_url: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=300&fit=crop',
    total_tickets: 12,
    sold_tickets: 11,
    ticket_tiers: [{ name: 'Tasting', price: 200, sale_mode: 'fixed' }],
    status: 'published',
    tag: '1 Limited Spots'
  },
  {
    id: '9',
    title: 'VIP Studio Session',
    influencer_name: 'RapStar',
    category: 'music',
    event_date: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
    location: 'Atlanta, GA',
    image_url: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=400&h=300&fit=crop',
    total_tickets: 6,
    sold_tickets: 5,
    ticket_tiers: [{ name: 'VIP', price: 350, sale_mode: 'fixed' }],
    status: 'published',
    tag: '1 Limited Spots'
  }
];

const privateEvents = [
  {
    id: '10',
    title: 'Bid to Win',
    subtitle: 'Dinner with Influencer',
    influencer_name: 'TopChef',
    category: 'food',
    image_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop',
    ticket_tiers: [{ name: 'Auction', price: 200, sale_mode: 'auction' }],
    status: 'published',
    badge: 'Bidding Now',
    badgeColor: 'bg-orange-500'
  },
  {
    id: '11',
    title: 'Behind-the-Scenes',
    subtitle: 'Hangout',
    influencer_name: 'MovieStar',
    category: 'lifestyle',
    image_url: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=300&fit=crop',
    ticket_tiers: [{ name: 'Invite', price: 0, sale_mode: 'fixed' }],
    status: 'published',
    badge: 'Invite-Only',
    badgeColor: 'bg-blue-500'
  },
  {
    id: '12',
    title: 'Luxury Yacht Party',
    subtitle: 'Sunset Cruise',
    influencer_name: 'LuxLife',
    category: 'lifestyle',
    image_url: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400&h=300&fit=crop',
    ticket_tiers: [{ name: 'Invite', price: 0, sale_mode: 'fixed' }],
    status: 'published',
    badge: 'Invite-Only',
    badgeColor: 'bg-blue-500'
  }
];

const hotSaleEvents = [
  {
    id: '13',
    title: 'E-Sports Tournament',
    influencer_name: 'ProGamer',
    category: 'gaming',
    image_url: 'https://images.unsplash.com/photo-1542751110-97427bbecf20?w=400&h=300&fit=crop',
    originalPrice: 105,
    salePrice: 75,
    status: 'published'
  },
  {
    id: '14',
    title: 'Acoustic Night Live',
    influencer_name: 'SingerSam',
    category: 'music',
    image_url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=300&fit=crop',
    originalPrice: 150,
    salePrice: 100,
    status: 'published'
  },
  {
    id: '15',
    title: 'Fitness Bootcamp',
    influencer_name: 'FitCoach',
    category: 'fitness',
    image_url: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop',
    originalPrice: 80,
    salePrice: 40,
    status: 'published'
  }
];

const trendingEvents = [
  { id: '16', title: 'Bid to Win', subtitle: 'Dinner with Influencer', image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=100&h=100&fit=crop' },
  { id: '17', title: 'Behind-the-Scenes', subtitle: 'Hangout', image: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=100&h=100&fit=crop' },
  { id: '18', title: 'Fashion Show', subtitle: 'Extravaganza', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop' },
  { id: '19', title: 'Fan Q&A', subtitle: 'with Sarah Vlogs', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop' }
];

const popularEvents = [
  { id: '20', title: 'Meet & Greet with Soccer Legend', date: 'Mar 25, 5 PM', image: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=100&h=100&fit=crop' },
  { id: '21', title: 'Comedy Night with Kevin LOL', date: 'Mar 23, 5PM', image: 'https://images.unsplash.com/photo-1527224857830-43a7acc85260?w=100&h=100&fit=crop' },
  { id: '22', title: 'Fashion Show Extravaganza', date: 'July 2, 9PM', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop' },
  { id: '23', title: 'Fan Q&A with Sarah Vlogs', date: 'May 12, 8PM', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop' }
];

const categories = [
  { id: 'gaming', name: 'Gaming', icon: Gamepad2 },
  { id: 'beauty', name: 'Beauty', icon: Sparkles },
  { id: 'music', name: 'Music', icon: Music },
  { id: 'comedy', name: 'Comedy', icon: Laugh },
  { id: 'fitness', name: 'Fitness', icon: Dumbbell },
  { id: 'food', name: 'Food', icon: Utensils },
  { id: 'tech', name: 'Tech', icon: Laptop },
  { id: 'lifestyle', name: 'Lifestyle', icon: Heart }
];

// Event Card Component - matches reference design
function EventCard({ event }) {
  const lowestPrice = event.ticket_tiers?.length > 0 
    ? Math.min(...event.ticket_tiers.map(t => t.price))
    : null;

  return (
    <Link 
      to={`/events/${event.id}`}
      className="group block min-w-[200px] max-w-[220px] flex-shrink-0"
      data-testid={`event-card-${event.id}`}
    >
      <div className="relative rounded-xl overflow-hidden">
        <img 
          src={event.image_url}
          alt={event.title}
          className="w-full aspect-[4/3] object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Content overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-heading font-semibold text-white text-sm line-clamp-1">
            {event.title}
          </h3>
          <p className="text-white/80 text-xs mt-0.5">with {event.influencer_name}</p>
          
          <div className="flex items-center gap-3 mt-2 text-white/70 text-xs">
            {event.event_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {format(new Date(event.event_date), 'MMM d')}
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {event.location.split(',')[0]}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Price below card */}
      {lowestPrice !== null && (
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">from</span>
          <span className="font-heading font-bold text-primary">${lowestPrice}</span>
        </div>
      )}
    </Link>
  );
}

// Small Intimate Card
function SmallIntimateCard({ event }) {
  return (
    <Link 
      to={`/events/${event.id}`}
      className="group block min-w-[180px] max-w-[200px] flex-shrink-0"
      data-testid={`intimate-card-${event.id}`}
    >
      <div className="relative rounded-xl overflow-hidden">
        <img 
          src={event.image_url}
          alt={event.title}
          className="w-full aspect-[4/3] object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-heading font-semibold text-white text-sm line-clamp-1">
            {event.title}
          </h3>
          <p className="text-white/80 text-xs mt-0.5">with {event.influencer_name}</p>
          {event.tag && (
            <p className="text-orange-400 text-xs mt-1 flex items-center gap-1">
              <Users className="w-3 h-3" />
              {event.tag}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

// Private Event Card
function PrivateEventCard({ event }) {
  return (
    <Link 
      to={`/events/${event.id}`}
      className="group block min-w-[220px] max-w-[260px] flex-shrink-0"
      data-testid={`private-card-${event.id}`}
    >
      <div className="relative rounded-xl overflow-hidden">
        <img 
          src={event.image_url}
          alt={event.title}
          className="w-full aspect-[3/2] object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-heading font-bold text-white text-base">
            {event.title}
          </h3>
          {event.subtitle && (
            <p className="text-white/90 text-sm">{event.subtitle}</p>
          )}
          <div className="mt-2 flex items-center justify-between">
            <span className="text-white/70 text-xs">{event.badge === 'Bidding Now' ? 'Bidding Now' : 'Invite-Only'}</span>
            <Badge className={`${event.badgeColor} text-white text-xs border-0`}>
              {event.badge}
            </Badge>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Hot Sale Card
function HotSaleCard({ event }) {
  return (
    <Link 
      to={`/events/${event.id}`}
      className="group block min-w-[200px] max-w-[220px] flex-shrink-0"
      data-testid={`sale-card-${event.id}`}
    >
      <div className="relative rounded-xl overflow-hidden">
        <img 
          src={event.image_url}
          alt={event.title}
          className="w-full aspect-[4/3] object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="font-heading font-semibold text-white text-sm line-clamp-1">
            {event.title}
          </h3>
          
          <div className="mt-2 flex items-center justify-between">
            <span className="text-white/50 text-sm line-through">${event.originalPrice}</span>
            <span className="bg-primary text-white font-bold px-2 py-1 rounded text-sm">
              ${event.salePrice}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Trending Sidebar Card
function TrendingSidebarCard({ event }) {
  return (
    <Link 
      to={`/events/${event.id}`}
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
      data-testid={`trending-${event.id}`}
    >
      <img 
        src={event.image}
        alt={event.title}
        className="w-14 h-14 rounded-lg object-cover"
      />
      <div>
        <h4 className="font-medium text-sm text-white">{event.title}</h4>
        <p className="text-slate-400 text-xs">{event.subtitle}</p>
      </div>
    </Link>
  );
}

// Popular Sidebar Card
function PopularSidebarCard({ event }) {
  return (
    <Link 
      to={`/events/${event.id}`}
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
      data-testid={`popular-${event.id}`}
    >
      <img 
        src={event.image}
        alt={event.title}
        className="w-10 h-10 rounded-full object-cover"
      />
      <div>
        <h4 className="font-medium text-sm text-white line-clamp-1">{event.title}</h4>
        <p className="text-slate-400 text-xs">{event.date}</p>
      </div>
    </Link>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isInfluencer } = useAuth();
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/featured`).catch(() => ({ data: [] }));
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section - Matching reference image */}
      <section className="relative h-[400px] overflow-hidden">
        {/* Background with city lights/bokeh effect */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1920&h=600&fit=crop')` 
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/60" />
        
        {/* Hero content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-xl">
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
              {t('landing.heroTitle')}
            </h1>
            <p className="text-slate-300 text-lg mt-3">
              {t('landing.heroSubtitle')}
            </p>
            
            <div className="flex gap-3 mt-6">
              {isAuthenticated ? (
                isInfluencer ? (
                  <Link to="/dashboard" data-testid="hero-dashboard-btn">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-6 gap-2">
                      <LayoutDashboard className="w-5 h-5" />
                      {t('nav.dashboard')}
                    </Button>
                  </Link>
                ) : (
                  <Link to="/my-tickets" data-testid="hero-following-btn">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-6 gap-2">
                      <Users className="w-5 h-5" />
                      {t('nav.myTickets')}
                    </Button>
                  </Link>
                )
              ) : (
                <Link to="/signup" data-testid="hero-signup-btn">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-6">
                    {t('landing.signUpNow')}
                  </Button>
                </Link>
              )}
              <Link to="/events" data-testid="hero-browse-btn">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6">
                  {t('landing.browseEvents')}
                </Button>
              </Link>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mt-6 max-w-md">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={t('common.search') + "..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-24 py-6 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-full backdrop-blur-sm"
                  data-testid="hero-search-input"
                />
                <Button 
                  type="submit"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-4"
                  data-testid="hero-search-btn"
                >
                  {t('common.search')}
                </Button>
              </div>
            </form>
          </div>
          
          {/* Hero image of people - right side */}
          <div className="hidden lg:block absolute right-0 bottom-0 h-full w-1/2">
            <img 
              src="https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=800&h=600&fit=crop&crop=faces"
              alt="Happy fans"
              className="h-full w-full object-cover object-top"
              style={{ maskImage: 'linear-gradient(to left, black 50%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to left, black 50%, transparent 100%)' }}
            />
          </div>
        </div>
      </section>

      {/* Categories Bar */}
      <section className="bg-slate-900/50 border-y border-white/5 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <Link 
                  key={cat.id}
                  to={`/events?category=${cat.id}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-primary/50 hover:bg-white/10 transition-all whitespace-nowrap"
                  data-testid={`category-${cat.id}`}
                >
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm text-slate-300">{cat.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0 space-y-10">
            
            {/* New & Upcoming Section */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h2 className="font-heading font-semibold text-xl text-white">{t('landing.newUpcoming')}</h2>
                  <Badge variant="outline" className="text-primary border-primary/30 text-xs">
                    {t('common.seeAll')}
                  </Badge>
                </div>
                <Link to="/events" className="text-primary text-sm hover:underline flex items-center gap-1">
                  {t('common.seeAll')} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {events.length > 0 ? events.slice(0, 5).map((event) => (
                  <EventCard key={event.id} event={event} />
                )) : (
                  <div className="flex-1 text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('landing.noEventsYet')}</p>
                  </div>
                )}
              </div>
            </section>

            {/* Small & Intimate Section */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-xl text-white">{t('landing.smallIntimate')}</h2>
                <Link to="/events?type=intimate" className="text-primary text-sm hover:underline flex items-center gap-1">
                  {t('common.seeAll')} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {events.filter(e => e.total_tickets <= 20).length > 0 ? 
                  events.filter(e => e.total_tickets <= 20).slice(0, 4).map((event) => (
                    <SmallIntimateCard key={event.id} event={event} />
                  )) : (
                  <div className="flex-1 text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t('landing.noIntimateEvents')}</p>
                  </div>
                )}
              </div>
            </section>

            {/* Exclusive Private Events Section */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-xl text-white">{t('landing.exclusivePrivate')}</h2>
                <Link to="/events?type=private" className="text-primary text-sm hover:underline flex items-center gap-1">
                  See All <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {events.filter(e => e.ticket_tiers?.some(t => t.sale_mode === 'auction')).length > 0 ?
                  events.filter(e => e.ticket_tiers?.some(t => t.sale_mode === 'auction')).slice(0, 3).map((event) => (
                    <PrivateEventCard key={event.id} event={event} />
                  )) : (
                  <div className="flex-1 text-center py-8 text-muted-foreground">
                    <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No exclusive events available yet.</p>
                  </div>
                )}
              </div>
            </section>

            {/* Hot Tickets on Sale Section */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary px-4 py-1 rounded">
                  <h2 className="font-heading font-bold text-white">Hot Tickets on Sale!</h2>
                </div>
              </div>
              
              <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {events.slice(0, 3).length > 0 ?
                  events.slice(0, 3).map((event) => (
                    <HotSaleCard key={event.id} event={event} />
                  )) : (
                  <div className="flex-1 text-center py-8 text-muted-foreground">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hot tickets available yet.</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Right Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0 space-y-8">
            {/* Trending Now */}
            <div className="bg-slate-900/50 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-white flex items-center gap-2">
                  Trending Now
                </h3>
                <Link to="/events?sort=trending" className="text-primary text-xs hover:underline">
                  See All &gt;
                </Link>
              </div>
              
              <div className="space-y-2">
                {events.slice(0, 4).length > 0 ? events.slice(0, 4).map((event) => (
                  <TrendingSidebarCard key={event.id} event={event} />
                )) : (
                  <p className="text-slate-500 text-sm text-center py-4">No trending events</p>
                )}
              </div>
            </div>

            {/* Popular Events */}
            <div className="bg-slate-900/50 border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-white">Popular Events</h3>
                <Link to="/events?sort=popular" className="text-primary text-xs hover:underline">
                  See All &gt;
                </Link>
              </div>
              
              <div className="space-y-2">
                {events.slice(0, 4).length > 0 ? events.slice(0, 4).map((event) => (
                  <PopularSidebarCard key={event.id} event={event} />
                )) : (
                  <p className="text-slate-500 text-sm text-center py-4">No popular events</p>
                )}
              </div>
            </div>

            {/* Creator CTA */}
            {isInfluencer ? (
              <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30 rounded-xl p-4">
                <LayoutDashboard className="w-8 h-8 text-green-400 mb-3" />
                <h3 className="font-heading font-bold text-white mb-2">Creator Dashboard</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Manage your events and view analytics.
                </p>
                <Link to="/dashboard" data-testid="sidebar-dashboard-link">
                  <Button className="w-full gap-2 bg-green-600 hover:bg-green-700" size="sm">
                    <LayoutDashboard className="w-4 h-4" />
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-4">
                <Sparkles className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-heading font-bold text-white mb-2">Are You a Creator?</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Start hosting events and connect with your fans.
                </p>
                <Link to="/influencer-signup" data-testid="sidebar-creator-cta">
                  <Button className="w-full gap-2" size="sm">
                    <Sparkles className="w-4 h-4" />
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
